// QR Kiosk v2 — KV2-2 presentational atoms shared by list + detail (+ KV2-3 later).
// KDate (null-guarded Moment — bug #6 / PG-11), UrgencyTag (PG-7 pip),
// ReportStatusPill (KV2-2-b tones), MetaCell (ReactNode, em-dash for absent).
// Tokens only; no MUI/dt/hex. react-moment is sanctioned (ticket §2.3).
import React from "react";
import Moment from "react-moment";
import useTranslation from "../../../shared/hooks/useTranslation";
import { StatusPip, PipTone } from "../shared/StatusPip";
import { URGENCY_META, REPORT_DATE_FORMAT } from "./reportUtils";
import { localizeDateFormat } from "../shared/dateFormat";

const EM_DASH = "—";

export interface KDateProps {
  date?: Date | string | null;
  format?: string;
}
// bug #6 / PG-11: the ONLY way a KV2-2 file renders a report date. A bare <Moment>
// renders "now" for null — that is a spec violation. Null/undefined -> em-dash.
// The pattern follows the UI language (Italian dd/mm/yyyy — see shared/dateFormat.ts).
export function KDate({ date, format = REPORT_DATE_FORMAT }: KDateProps) {
  const { i18n } = useTranslation("public");
  if (!date) return <>{EM_DASH}</>;
  return <Moment date={date} format={localizeDateFormat(format, i18n.language)} />;
}

export interface UrgencyTagProps {
  urgency?: number | null;
}
// PG-7 pip (designer UrgDot). Renders NOTHING for null / value 3 / any unmapped value (D9).
export function UrgencyTag({ urgency }: UrgencyTagProps) {
  const { translate } = useTranslation("public");
  if (urgency == null || !URGENCY_META[urgency]) return null;
  const { labelKey, tone } = URGENCY_META[urgency];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: `color-mix(in oklab, var(${tone}) 80%, var(--ink))`,
      }}
    >
      <StatusPip tone={tone} size={7} />
      {translate(labelKey)}
    </span>
  );
}

export interface ReportStatusPillProps {
  solved: boolean;
}
// Detail status pill (KV2-2-b): InProgress = amber, Solved = green.
export function ReportStatusPill({ solved }: ReportStatusPillProps) {
  const { translate } = useTranslation("public");
  const tone: PipTone = solved ? "--green" : "--amber";
  const label = translate(solved ? "kioskV2StatusSolved" : "kioskV2StatusInProgress");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        height: 28,
        padding: "0 12px",
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 700,
        background: `color-mix(in oklab, var(${tone}) 13%, var(--card))`,
        border: `1px solid color-mix(in oklab, var(${tone}) 38%, var(--border))`,
        color: `color-mix(in oklab, var(${tone}) 78%, var(--ink))`,
      }}
    >
      <StatusPip tone={tone} size={8} />
      {label}
    </span>
  );
}

export interface MetaCellProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}
// Designer MetaCell. value is a ReactNode; null/undefined/empty-string -> em-dash.
// (A null/empty check — never `value || …` — so a valid <KDate> element is not displaced.)
export function MetaCell({ label, value, mono = true }: MetaCellProps) {
  const isEmpty = value == null || value === "";
  return (
    <div style={{ padding: "9px 0" }}>
      <div className="eyebrow" style={{ marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          fontSize: 14.5,
          fontWeight: 600,
          color: "var(--ink)",
        }}
      >
        {isEmpty ? EM_DASH : value}
      </div>
    </div>
  );
}
